import { useMemo, useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
    Box,
    Flex,
    HStack,
    VStack,
    Badge,
    Text,
    Accordion,
    CloseButton,
} from '@chakra-ui/react';
import { Button, ConfirmationDialog } from '@/components/v4';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { useQuery } from 'hooks/useCustomQuery';
import useGetCompanyServices from '@/queries/appAuth/useGetCompanyServices';
import useGetAvailableNumbersDropdown from 'pages/accounts/users/queries/useGetAvailableNumbersDropdown';
import getEightByEightCatalogue from 'API/ServiceProfiles/getEightByEightCatalogue';
import getCompanyPlatformUsers from '@/API/PlatformUser/getCompanyPlatformUsers';
import { isRequiredRule } from 'utils/eightByEightCatalog';
import { AccountAvatar } from 'pages/accounts/users/new/cellRenderer/AccountAvatar';
import {
    createUserProfile,
    deleteUserProfile,
    getExtendedAttributes,
    getLicenses,
    getSites,
    getUserProfile,
    updateUserProfile,
    updatePlatformUser,
    getRingGroupMembers,
    createRingGroupMembership,
    updateRingGroupMembership,
    deleteRingGroupMembership,
} from 'pages/services/eightByEight/userManagement/api';
import RingGroupDetailsSection from './RingGroupDetailsSection';
import RingGroupLicencesSection from './RingGroupLicencesSection';
import RingGroupPhoneNumbersSection from './RingGroupPhoneNumbersSection';
import RingGroupExtendedAttributesSection from './RingGroupExtendedAttributesSection';
import RingGroupMembersSection from './RingGroupMembersSection';

const NOT_ALLOCATED_OPTION = {
    numberID: '',
    telephoneNumber: '— Not Allocated —',
};

const MEMBER_PAGE_SIZE = 10;

const getProfileLicenses = (profile) => profile?.licenses || [];

const getProfileNumber = (profile) => profile?.platformNumber || profile;

const getProfileNumberId = (profile) =>
    getProfileNumber(profile)?.numberID || profile?.numberID || '';

const getLicenseId = (license) =>
    license?.id || license?.itemID || license?.catalogItemID;

const getSelectedLicenceIds = (...profiles) =>
    profiles
        .map(getProfileLicenses)
        .find((licenses) => licenses.length > 0)
        ?.map(getLicenseId)
        .filter(Boolean) || [];

// Response may be a flat array or an object wrapping { platformUsers: [] }.
const normalisePlatformUserList = (raw) =>
    Array.isArray(raw) ? raw : raw?.platformUsers || [];

const getPlatformUserId = (user) => user?.id || user?.primaryUserID || '';

// Fields may be nested under primaryUser instead of flat, same duality as `pu` below.
const getPlatformUserDisplayName = (user) =>
    user?.displayName ||
    user?.primaryUser?.displayName ||
    [user?.givenName, user?.surname].filter(Boolean).join(' ') ||
    user?.userPrincipalName ||
    user?.email ||
    'Unnamed User';

const getPlatformUserNumber = (user) =>
    user?.userProfiles?.[0]?.platformNumber?.telephoneNumber ||
    user?.primaryUser?.userProfiles?.[0]?.platformNumber?.telephoneNumber ||
    '';

const getPlatformUserExtension = (user) =>
    user?.platformExtension || user?.primaryUser?.platformExtension || '';

// Normalise to a { lowercasedName: value } map since attributeName casing isn't reliable.
const normaliseExtendedAttributeValues = (raw) => {
    if (!raw) return {};
    const list = Array.isArray(raw)
        ? raw
        : Object.entries(raw).map(([name, value]) => ({ name, value }));
    return list.reduce((acc, entry) => {
        const name = entry?.attributeName || entry?.name;
        if (name) {
            acc[name.toLowerCase()] = entry?.value ?? entry?.attributeValue;
        }
        return acc;
    }, {});
};

// Real value first (if any), falling back to the schema's defaultValue.
const buildInitialExtendedAttributes = (definitions, values) => {
    const valueMap = normaliseExtendedAttributeValues(values);
    const map = {};
    definitions.forEach((attribute) => {
        if (attribute?.isActive === false) return;
        const name = attribute.attributeName;
        map[name] =
            valueMap[name?.toLowerCase()] ?? attribute.defaultValue ?? '';
    });
    return map;
};

const ringGroupValidationSchema = Yup.object({
    name: Yup.string().required('Name is required'),
    platformExtension: Yup.string().matches(
        /^\d*$/,
        'Extension must contain only numbers',
    ),
});

export default function RingGroupEditContent({
    selectedPlatform,
    expandedPlatformUsers,
    onClose,
    onSave,
    darkMode,
}) {
    const { currentCompany } = useSelector((state) => state.navigationLists);
    const { requiredScope } = useSelector((state) => state.login);

    const { data: companyServices = [] } =
        useGetCompanyServices(currentCompany);
    const eightByEightService = companyServices.find(
        (s) => s.serviceType === 'EightByEight',
    );
    const serviceId = eightByEightService?.id;
    const platformId = eightByEightService?.platformID;

    // The 8x8 platform user ID used by the UserProfiles/RingGroup Membership endpoints.
    const ringGroupId = selectedPlatform?.primaryUserID || selectedPlatform?.id;

    // primaryUser holds the authoritative per-instance fields; selectedPlatform is a fallback.
    const pu = selectedPlatform?.primaryUser || selectedPlatform;

    const displayName = pu?.displayName || 'Ring Group';

    const currentSiteId =
        pu?.siteID ||
        pu?.siteId ||
        selectedPlatform?.siteID ||
        selectedPlatform?.siteId ||
        '';

    const email =
        pu?.userPrincipalName ||
        pu?.email ||
        selectedPlatform?.userPrincipalName ||
        selectedPlatform?.email ||
        '';

    const { data: userProfile } = useQuery({
        queryKey: ['8x8-drawer-userProfile', ringGroupId],
        queryFn: () =>
            getUserProfile(ringGroupId, requiredScope).then((r) => r.data),
        enabled: !!ringGroupId,
        staleTime: 0,
        cacheTime: 0,
    });

    const { data: rawLicenses = [] } = useQuery({
        queryKey: ['8x8-drawer-licences', serviceId],
        queryFn: () =>
            getLicenses(serviceId, requiredScope).then((r) => r.data),
        enabled: !!serviceId,
        staleTime: 0,
        cacheTime: 0,
    });

    const { data: sites = [] } = useQuery({
        queryKey: ['8x8-drawer-sites', serviceId],
        queryFn: () => getSites(serviceId, requiredScope).then((r) => r.data),
        enabled: !!serviceId,
        staleTime: 0,
        cacheTime: 0,
    });
    const isSingleSite = sites.length === 1;

    // Schema/definitions only — real per-instance values come from pu.extendedAttributes.
    const { data: extendedAttributeDefinitions = [] } = useQuery({
        queryKey: ['8x8-drawer-extended-attributes', platformId],
        queryFn: () =>
            getExtendedAttributes(platformId, 'RingGroup', requiredScope).then(
                (r) => r.data,
            ),
        enabled: !!platformId,
        staleTime: 0,
        cacheTime: 0,
    });

    // The grid list this drawer opens from never populates extendedAttributes; expandedPlatformUsers does.
    const freshPlatformUser = expandedPlatformUsers?.find(
        (platform) => platform?.platformID === selectedPlatform?.platformID,
    );

    const extendedAttributeValues =
        freshPlatformUser?.extendedAttributes ||
        pu?.extendedAttributes ||
        selectedPlatform?.extendedAttributes;

    const initialExtendedAttributes = useMemo(
        () =>
            buildInitialExtendedAttributes(
                extendedAttributeDefinitions,
                extendedAttributeValues,
            ),
        [extendedAttributeDefinitions, extendedAttributeValues],
    );

    // getLicenses only has flat quantities; hierarchy comes from the company catalogue instead.
    const { data: catalogueItems = [] } = useQuery({
        queryKey: ['8x8-drawer-catalogue', currentCompany],
        queryFn: () =>
            getEightByEightCatalogue({
                level: 'company',
                companyID: currentCompany,
            }).then((r) => r.data || []),
        enabled: !!currentCompany,
        staleTime: 0,
        cacheTime: 0,
    });

    // Merge catalogue hierarchy with usage quantities into the shape AddUserLicenceTree expects.
    const catalogue = useMemo(() => {
        const usageByItemId = {};
        rawLicenses.forEach((l) => {
            const itemId = l.catalogItemID ?? l.itemID ?? l.id;
            if (itemId) usageByItemId[itemId] = l;
        });
        return catalogueItems.map((item) => {
            const usage = usageByItemId[item.id];
            return {
                id: item.id,
                name: item.name,
                description: item.description || '',
                parentId: item.parentId || null,
                active: usage?.usedQuantity ?? 0,
                total: usage?.allowedQuantity ?? 0,
                required: isRequiredRule(item),
            };
        });
    }, [catalogueItems, rawLicenses]);

    const selectedUserProfile = selectedPlatform?.userProfiles?.[0];
    const primaryNumberId =
        getProfileNumberId(selectedUserProfile) ||
        getProfileNumberId(userProfile);
    const resolvedPlatformNumber = primaryNumberId
        ? getProfileNumber(selectedUserProfile) || getProfileNumber(userProfile)
        : undefined;
    const platformNumber = primaryNumberId
        ? {
              ...resolvedPlatformNumber,
              numberID: primaryNumberId,
          }
        : undefined;

    const [rawNumberList, setRawNumberList] = useState([]);
    const { isLoading: isLoadingNumbers } = useGetAvailableNumbersDropdown(
        currentCompany,
        platformNumber,
        platformId,
        setRawNumberList,
    );
    // Strip the "- Not Allocated -" sentinel (it uses id:'null', not numberID)
    const numberList = rawNumberList.filter((n) => n.id !== 'null');

    // Full number list: available numbers + currently assigned number (if not in list) + Not Allocated
    const fullNumberList = useMemo(() => {
        const list = [...numberList];
        if (
            userProfile?.numberID &&
            !list.some((n) => n.numberID === userProfile.numberID)
        ) {
            list.unshift({
                numberID: userProfile.numberID,
                telephoneNumber: userProfile.numberID,
            });
        }
        list.push(NOT_ALLOCATED_OPTION);
        return list;
    }, [numberList, userProfile?.numberID]);

    // Add/Remove only stages changes locally; handleSubmit batches the actual API calls on Save.
    const [pendingMemberChanges, setPendingMemberChanges] = useState({});
    const [memberSearch, setMemberSearch] = useState('');
    const [memberPage, setMemberPage] = useState(1);

    const handleMemberSearchChange = (value) => {
        setMemberSearch(value);
        setMemberPage(1);
    };

    const {
        data: members = [],
        isLoading: isLoadingMembers,
        refetch: refetchMembers,
    } = useQuery({
        queryKey: ['ringgroup-drawer-members', ringGroupId],
        queryFn: () =>
            getRingGroupMembers(ringGroupId, requiredScope).then((r) => r.data),
        enabled: !!ringGroupId,
        staleTime: 0,
        cacheTime: 0,
    });

    const { data: companyPlatformUsers = [], isLoading: isLoadingCandidates } =
        useQuery({
            queryKey: [
                'ringgroup-drawer-candidates',
                currentCompany,
                platformId,
            ],
            queryFn: () =>
                getCompanyPlatformUsers(
                    currentCompany,
                    platformId,
                    requiredScope,
                ).then(normalisePlatformUserList),
            enabled: !!currentCompany && !!platformId,
            staleTime: 0,
            cacheTime: 0,
        });

    const getMemberId = (member) =>
        member?.memberUserID ||
        member?.memberUser?.id ||
        member?.memberID ||
        member?.platformUserID ||
        member?.id ||
        '';

    const getMemberVoicemailAccess = (member) =>
        member?.voiceMailAccessEnabled ?? false;

    const memberIdSet = useMemo(
        () => new Set(members.map(getMemberId).filter(Boolean)),
        [members],
    );

    const committedVoicemailMap = useMemo(() => {
        const map = {};
        members.forEach((member) => {
            const id = getMemberId(member);
            if (id) map[id] = getMemberVoicemailAccess(member);
        });
        return map;
    }, [members]);

    // Per-row Voicemail Access overrides, staged the same way as Add/Remove until Save.
    const [voicemailOverrides, setVoicemailOverrides] = useState({});

    // Every 8x8 platform user except other ring groups and this one; extension-less users are still eligible.
    const eligibleUsers = useMemo(
        () =>
            companyPlatformUsers.filter((user) => {
                const userId = getPlatformUserId(user);
                if (!userId || userId === ringGroupId) return false;
                if (user?.userType === 'RingGroup') return false;
                return true;
            }),
        [companyPlatformUsers, ringGroupId],
    );

    const combinedMemberRows = useMemo(
        () =>
            eligibleUsers
                .map((user) => {
                    const id = getPlatformUserId(user);
                    const committedIsMember = memberIdSet.has(id);
                    const pendingAction = pendingMemberChanges[id];
                    const isMember = pendingAction
                        ? pendingAction === 'add'
                        : committedIsMember;
                    const voicemailAccess =
                        voicemailOverrides[id] ??
                        committedVoicemailMap[id] ??
                        false;
                    return {
                        id,
                        name: getPlatformUserDisplayName(user),
                        phoneNumber: getPlatformUserNumber(user) || '—',
                        extension: getPlatformUserExtension(user) || '—',
                        committedIsMember,
                        isMember,
                        isPending: !!pendingAction,
                        voicemailAccess,
                    };
                })
                .sort((a, b) => a.name.localeCompare(b.name)),
        [
            eligibleUsers,
            memberIdSet,
            pendingMemberChanges,
            voicemailOverrides,
            committedVoicemailMap,
        ],
    );

    const phoneFilteredRows = useMemo(
        () => combinedMemberRows.filter((row) => row.phoneNumber !== '—'),
        [combinedMemberRows],
    );
    const hasEligibleMembersWithPhoneNumber = phoneFilteredRows.length > 0;

    const filteredMemberRows = useMemo(() => {
        const term = memberSearch.trim().toLowerCase();
        if (!term) return phoneFilteredRows;
        return phoneFilteredRows.filter(
            (row) =>
                row.name.toLowerCase().includes(term) ||
                row.phoneNumber.toLowerCase().includes(term),
        );
    }, [phoneFilteredRows, memberSearch]);

    const totalMemberPages = Math.max(
        1,
        Math.ceil(filteredMemberRows.length / MEMBER_PAGE_SIZE),
    );
    const safeMemberPage = Math.min(memberPage, totalMemberPages);
    const paginatedMemberRows = useMemo(
        () =>
            filteredMemberRows.slice(
                (safeMemberPage - 1) * MEMBER_PAGE_SIZE,
                safeMemberPage * MEMBER_PAGE_SIZE,
            ),
        [filteredMemberRows, safeMemberPage],
    );

    // Rows showing the "add" checkbox: not a committed, non-pending member (a
    // staged removal falls back here). Scoped to the current page, so
    // "select all" only affects what's visible.
    const checkboxRows = useMemo(
        () =>
            paginatedMemberRows.filter(
                (row) => !(row.committedIsMember && !row.isPending),
            ),
        [paginatedMemberRows],
    );
    const allCheckboxRowsChecked =
        checkboxRows.length > 0 && checkboxRows.every((row) => row.isMember);
    const someCheckboxRowsChecked = checkboxRows.some((row) => row.isMember);

    const toggleAllPending = () => {
        const targetChecked = !allCheckboxRowsChecked;
        checkboxRows.forEach((row) => {
            if (row.isMember !== targetChecked) toggleMemberPending(row.id);
        });
    };

    // Toggling only stages the change; toggling back before Save cancels it.
    const toggleMemberPending = (userId) => {
        setPendingMemberChanges((prev) => {
            const next = { ...prev };
            if (next[userId]) {
                delete next[userId];
            } else {
                next[userId] = memberIdSet.has(userId) ? 'remove' : 'add';
            }
            return next;
        });
    };

    const setVoicemailOverride = (userId, value) => {
        setVoicemailOverrides((prev) => ({
            ...prev,
            [userId]: value,
        }));
    };

    // Which member's Delete confirmation is currently open.
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);
    const [isDeletingMember, setIsDeletingMember] = useState(false);

    // Unlike Add/Remove and Voicemail Access, Delete fires immediately on
    // confirm rather than staging until the drawer's Save button.
    const handleConfirmDeleteMember = () => {
        const userId = deleteConfirmId;
        if (!userId) return;
        setIsDeletingMember(true);
        const voiceMailAccessEnabled =
            voicemailOverrides[userId] ??
            committedVoicemailMap[userId] ??
            false;
        deleteRingGroupMembership(
            {
                parentUserID: ringGroupId,
                memberUserID: userId,
                voiceMailAccessEnabled,
            },
            requiredScope,
        )
            .then(async () => {
                setVoicemailOverrides((prev) => {
                    const next = { ...prev };
                    delete next[userId];
                    return next;
                });
                await refetchMembers();
                toast.success('Member removed from ring group');
            })
            .catch((err) => {
                toast.error(
                    err?.response?.data?.message ||
                        err?.message ||
                        'Failed to remove this member.',
                );
            })
            .finally(() => setIsDeletingMember(false));
    };

    // Fires the actual create/delete/update calls for every staged change, only from handleSubmit.
    const applyPendingMemberChanges = async () => {
        // Members with no Add/Remove change but a toggled Voicemail Access still need an update call.
        const voicemailOnlyUpdates = Object.keys(voicemailOverrides).filter(
            (userId) =>
                !pendingMemberChanges[userId] &&
                memberIdSet.has(userId) &&
                voicemailOverrides[userId] !==
                    (committedVoicemailMap[userId] ?? false),
        );

        const entries = [
            ...Object.entries(pendingMemberChanges),
            ...voicemailOnlyUpdates.map((userId) => [userId, 'update']),
        ];
        if (entries.length === 0) return;

        const failed = {};
        const failureMessages = new Set();
        for (const [userId, action] of entries) {
            const voiceMailAccessEnabled =
                voicemailOverrides[userId] ??
                committedVoicemailMap[userId] ??
                false;
            try {
                if (action === 'add') {
                    // Only candidates with a phone number are ever offered for
                    // Add (see phoneFilteredRows), so no UserProfile
                    // provisioning is needed here.
                    await createRingGroupMembership(
                        {
                            parentUserID: ringGroupId,
                            memberUserID: userId,
                            voiceMailAccessEnabled,
                        },
                        requiredScope,
                    );
                } else if (action === 'remove') {
                    await deleteRingGroupMembership(
                        {
                            parentUserID: ringGroupId,
                            memberUserID: userId,
                            voiceMailAccessEnabled,
                        },
                        requiredScope,
                    );
                } else {
                    await updateRingGroupMembership(
                        {
                            parentUserID: ringGroupId,
                            memberUserID: userId,
                            voiceMailAccessEnabled,
                        },
                        requiredScope,
                    );
                }
            } catch (err) {
                failed[userId] = action;
                failureMessages.add(
                    err?.response?.data?.message ||
                        err?.message ||
                        'Failed to save this member change.',
                );
            }
        }

        const failedMembershipChanges = {};
        Object.entries(failed).forEach(([userId, action]) => {
            if (action === 'add' || action === 'remove') {
                failedMembershipChanges[userId] = action;
            }
        });
        setPendingMemberChanges(failedMembershipChanges);
        setVoicemailOverrides((prev) => {
            const next = { ...prev };
            entries.forEach(([userId]) => {
                if (!failed[userId]) delete next[userId];
            });
            return next;
        });
        await refetchMembers();

        const failedCount = Object.keys(failed).length;
        const succeededCount = entries.length - failedCount;
        if (succeededCount > 0) {
            toast.success(
                `${succeededCount} member change${
                    succeededCount === 1 ? '' : 's'
                } saved`,
            );
        }
        if (failedCount > 0) {
            failureMessages.forEach((message) => toast.error(message));
        }
    };

    const borderColor = darkMode ? 'dark.tcap-borderGray' : 'gray.200';
    const panelBg = darkMode ? 'dark.tcap' : 'white';
    const accordionBorderColor = darkMode ? 'dark.tcap-borderGray' : 'gray.200';

    const initialValues = {
        name: displayName,
        platformExtension:
            pu?.platformExtension ?? selectedPlatform?.platformExtension ?? '',
        siteId: currentSiteId,
        // Licences — pre-fill from userProfile
        selectedLicenceIds: getSelectedLicenceIds(
            selectedUserProfile,
            userProfile,
        ),
        // Phone Numbers — pre-fill from userProfile
        primaryNumberId,
        // Extended Attributes — pre-fill from real value or schema default
        extendedAttributes: initialExtendedAttributes,
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            const numberCleared = !!primaryNumberId && !values.primaryNumberId;

            // Step 1: Update Ring Group name / extension / extended attributes
            const extendedAttributesPayload = extendedAttributeDefinitions
                .filter((attribute) => attribute?.isActive !== false)
                .map((attribute) => ({
                    name: attribute.attributeName,
                    value:
                        values.extendedAttributes?.[attribute.attributeName] ??
                        '',
                }));

            await updatePlatformUser(
                {
                    id: ringGroupId,
                    platformID: platformId,
                    siteID: values.siteId || null,
                    email,
                    displayName: values.name.trim(),
                    userTypeID: 'RingGroup',
                    platformExtension: values.platformExtension,
                    managedBy: 0,
                    extendedAttributes: extendedAttributesPayload,
                },
                requiredScope,
            );

            // Step 2: DELETE if number was cleared
            if (numberCleared) {
                await deleteUserProfile(
                    primaryNumberId,
                    platformId,
                    ringGroupId,
                    requiredScope,
                );
            }

            // Step 3: PUT to sync number+licences, or licences-only if no number selected.
            const licences = values.selectedLicenceIds.map((id) => {
                const match = catalogue.find((l) => l.id === id);
                return {
                    id,
                    name: match?.name || '',
                    description: match?.description || null,
                };
            });

            const profilePayload = {
                platformUserID: ringGroupId,
                platformID: platformId,
                licenses: licences,
            };

            if (values.primaryNumberId) {
                const payloadWithNumber = {
                    ...profilePayload,
                    numberID: values.primaryNumberId,
                };

                if (primaryNumberId) {
                    await updateUserProfile(payloadWithNumber, requiredScope);
                } else {
                    await createUserProfile(payloadWithNumber, requiredScope);
                }
            } else if (!numberCleared && licences.length > 0) {
                // No number ever assigned — still persist licence selections
                if (primaryNumberId) {
                    await updateUserProfile(profilePayload, requiredScope);
                } else {
                    await createUserProfile(profilePayload, requiredScope);
                }
            }

            // Step 4: Apply any staged Members Add/Remove changes
            await applyPendingMemberChanges();

            toast.success('Ring Group updated successfully');
            if (typeof onSave === 'function') onSave(values);
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                err?.message ||
                'An error occurred. Please try again.';
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={ringGroupValidationSchema}
            onSubmit={handleSubmit}
            enableReinitialize>
            {({
                values,
                errors,
                touched,
                setFieldValue,
                handleSubmit: submitForm,
                isSubmitting,
            }) => {
                // Any root-level (no-parent) licence stands in for "base platform assigned".
                const hasBaseLicence = catalogue.some(
                    (item) =>
                        !item.parentId &&
                        values.selectedLicenceIds.includes(item.id),
                );
                return (
                    <Flex direction="column" h="100%" gap={0}>
                        {/* Header */}
                        <Flex
                            align="center"
                            justify="space-between"
                            pb={4}
                            mb={2}
                            borderBottomWidth="1px"
                            borderColor={borderColor}>
                            <HStack spacing={3}>
                                <AccountAvatar
                                    isRA
                                    size="md"
                                    formattedValue="Ring Group"
                                />
                                <VStack align="flex-start" spacing={0}>
                                    <HStack spacing={2}>
                                        <Text
                                            fontWeight="700"
                                            fontSize="lg"
                                            color={
                                                darkMode ? 'white' : 'gray.800'
                                            }>
                                            {displayName}
                                        </Text>
                                        <Badge
                                            colorScheme="gray"
                                            fontSize="10px"
                                            px={2}
                                            py="2px"
                                            borderRadius="md"
                                            textTransform="uppercase">
                                            Ring Group
                                        </Badge>
                                    </HStack>
                                    {email && (
                                        <Text fontSize="xs" color="gray.500">
                                            {email}
                                        </Text>
                                    )}
                                </VStack>
                            </HStack>
                            <CloseButton
                                size="md"
                                onClick={onClose}
                                color={darkMode ? 'white' : 'gray.600'}
                            />
                        </Flex>

                        {/* Accordion sections */}
                        <Box flex="1" overflowY="auto">
                            <Accordion
                                allowMultiple
                                defaultIndex={[0]}
                                borderColor={accordionBorderColor}>
                                <RingGroupDetailsSection
                                    errors={errors}
                                    touched={touched}
                                    values={values}
                                    setFieldValue={setFieldValue}
                                    sites={sites}
                                    isSingleSite={isSingleSite}
                                    darkMode={darkMode}
                                    panelBg={panelBg}
                                    accordionBorderColor={accordionBorderColor}
                                />

                                <RingGroupLicencesSection
                                    catalogue={catalogue}
                                    values={values}
                                    setFieldValue={setFieldValue}
                                    darkMode={darkMode}
                                    panelBg={panelBg}
                                    accordionBorderColor={accordionBorderColor}
                                />

                                <RingGroupPhoneNumbersSection
                                    values={values}
                                    setFieldValue={setFieldValue}
                                    fullNumberList={fullNumberList}
                                    isLoadingNumbers={isLoadingNumbers}
                                    hasBaseLicence={hasBaseLicence}
                                    darkMode={darkMode}
                                    panelBg={panelBg}
                                    accordionBorderColor={accordionBorderColor}
                                />

                                <RingGroupExtendedAttributesSection
                                    attributes={extendedAttributeDefinitions}
                                    darkMode={darkMode}
                                    panelBg={panelBg}
                                    accordionBorderColor={accordionBorderColor}
                                />

                                <RingGroupMembersSection
                                    memberSearch={memberSearch}
                                    setMemberSearch={handleMemberSearchChange}
                                    isLoadingMembers={isLoadingMembers}
                                    isLoadingCandidates={isLoadingCandidates}
                                    hasEligibleMembersWithPhoneNumber={
                                        hasEligibleMembersWithPhoneNumber
                                    }
                                    allCheckboxRowsChecked={
                                        allCheckboxRowsChecked
                                    }
                                    someCheckboxRowsChecked={
                                        someCheckboxRowsChecked
                                    }
                                    checkboxRows={checkboxRows}
                                    toggleAllPending={toggleAllPending}
                                    isSubmitting={
                                        isSubmitting || isDeletingMember
                                    }
                                    memberRows={paginatedMemberRows}
                                    isSearchResultEmpty={
                                        filteredMemberRows.length === 0
                                    }
                                    memberPage={safeMemberPage}
                                    totalMemberPages={totalMemberPages}
                                    onPrevMemberPage={() =>
                                        setMemberPage((p) => Math.max(1, p - 1))
                                    }
                                    onNextMemberPage={() =>
                                        setMemberPage((p) =>
                                            Math.min(totalMemberPages, p + 1),
                                        )
                                    }
                                    toggleMemberPending={toggleMemberPending}
                                    setVoicemailOverride={setVoicemailOverride}
                                    setDeleteConfirmId={setDeleteConfirmId}
                                    darkMode={darkMode}
                                    panelBg={panelBg}
                                    accordionBorderColor={accordionBorderColor}
                                />
                            </Accordion>
                        </Box>

                        <ConfirmationDialog
                            isOpen={!!deleteConfirmId}
                            onClose={() => setDeleteConfirmId(null)}
                            onConfirm={handleConfirmDeleteMember}
                            title="Remove Member"
                            message="Are you sure you want to remove this member from the ring group?"
                            confirmText="Remove"
                            confirmColorScheme="red"
                            isLoading={isDeletingMember}
                        />

                        {/* Footer buttons */}
                        <Flex
                            align="center"
                            justify="space-between"
                            pt={4}
                            mt={2}
                            borderTopWidth="1px"
                            borderColor={borderColor}>
                            <HStack spacing={3}>
                                <Button
                                    colorScheme="messenger"
                                    onClick={submitForm}
                                    isLoading={isSubmitting}
                                    data-walkthroughid="userEditDrawerSaveBtn">
                                    Save
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={onClose}
                                    isDisabled={isSubmitting}
                                    data-walkthroughid="userEditDrawerCancelBtn">
                                    Cancel
                                </Button>
                            </HStack>
                        </Flex>
                    </Flex>
                );
            }}
        </Formik>
    );
}
